import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateMenuColor, fetchMenuColor, updateGreyMenuColor, fetchGreyMenuColor } from '../../store/slices/usersSlice';
import { showToast } from '../utils/Toast';
import { useThemeColors } from '../../hooks/useThemeColors';

const AppearanceTab = () => {
  const dispatch = useDispatch();
  const { menuColor, customMenuColor, greyMenuColor, customGreyMenuColor, loading } = useSelector(state => state.users);
  const { buttonColors, greyMenuColors } = useThemeColors();
  const [selectedColor, setSelectedColor] = useState(menuColor);
  const [customColor, setCustomColor] = useState(customMenuColor || '#1d4ed8');
  const [showCustomPicker, setShowCustomPicker] = useState(false);
  const [selectedGreyColor, setSelectedGreyColor] = useState(greyMenuColor);
  const [customGreyColor, setCustomGreyColor] = useState(customGreyMenuColor || '#f9fafb');
  const [showGreyCustomPicker, setShowGreyCustomPicker] = useState(false);

  const colorOptions = [
    { 
      name: 'Ocean Blue', 
      value: 'blue', 
      gradient: 'from-blue-500 via-blue-600 to-blue-700',
      solid: '#1d4ed8',
      description: 'Professional and trustworthy'
    },
    { 
      name: 'Deep Indigo', 
      value: 'indigo', 
      gradient: 'from-indigo-500 via-indigo-600 to-indigo-700',
      solid: '#4338ca',
      description: 'Modern and sophisticated'
    },
    { 
      name: 'Royal Purple', 
      value: 'purple', 
      gradient: 'from-purple-500 via-purple-600 to-purple-700',
      solid: '#7c3aed',
      description: 'Creative and innovative'
    },
    { 
      name: 'Forest Green', 
      value: 'green', 
      gradient: 'from-green-500 via-green-600 to-green-700',
      solid: '#15803d',
      description: 'Natural and balanced'
    },
    { 
      name: 'Crimson Red', 
      value: 'red', 
      gradient: 'from-red-500 via-red-600 to-red-700',
      solid: '#b91c1c',
      description: 'Bold and energetic'
    },
    { 
      name: 'Sunset Orange', 
      value: 'orange', 
      gradient: 'from-orange-500 via-orange-600 to-orange-700',
      solid: '#c2410c',
      description: 'Warm and inviting'
    },
    { 
      name: 'Teal Breeze', 
      value: 'teal', 
      gradient: 'from-teal-500 via-teal-600 to-teal-700',
      solid: '#0f766e',
      description: 'Fresh and calming'
    },
    { 
      name: 'Slate Gray', 
      value: 'gray', 
      gradient: 'from-gray-500 via-gray-600 to-gray-700',
      solid: '#374151',
      description: 'Neutral and timeless'
    }
  ];

  useEffect(() => {
    dispatch(fetchMenuColor());
    dispatch(fetchGreyMenuColor());
  }, [dispatch]);

  useEffect(() => {
    setSelectedColor(menuColor);
    if (customMenuColor) {
      setCustomColor(customMenuColor);
    }
    setSelectedGreyColor(greyMenuColor);
    if (customGreyMenuColor) {
      setCustomGreyColor(customGreyMenuColor);
    } else {
      setCustomGreyColor('#f9fafb'); // Default to table header color
    }
  }, [menuColor, customMenuColor, greyMenuColor, customGreyMenuColor]);

  const handleColorChange = async (color) => {
    setSelectedColor(color);
    setShowCustomPicker(false);
    try {
      await dispatch(updateMenuColor({ menuColor: color, customMenuColor: null })).unwrap();
      showToast('Theme updated successfully!', 'success');
    } catch (error) {
      showToast('Failed to update theme', 'error');
      setSelectedColor(menuColor);
    }
  };

  const handleCustomColorApply = async () => {
    // Validate hex color format
    const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    if (!hexColorRegex.test(customColor)) {
      showToast('Please enter a valid hex color code (e.g., #1d4ed8)', 'error');
      return;
    }

    setSelectedColor('custom');
    setShowCustomPicker(false);
    try {
      await dispatch(updateMenuColor({ menuColor: 'custom', customMenuColor: customColor })).unwrap();
      showToast('Custom theme applied successfully!', 'success');
    } catch (error) {
      showToast('Failed to apply custom theme', 'error');
      setSelectedColor(menuColor);
    }
  };

  const getCurrentColorInfo = () => {
    if (selectedColor === 'custom') {
      return {
        name: 'Custom Color',
        value: 'custom',
        gradient: '',
        solid: customColor,
        description: 'Your personalized theme'
      };
    }
    return colorOptions.find(option => option.value === selectedColor) || colorOptions[0];
  };

  const handleGreyColorChange = async (color) => {
    setSelectedGreyColor(color);
    setShowGreyCustomPicker(false);
    try {
      await dispatch(updateGreyMenuColor({ greyMenuColor: color, customGreyMenuColor: null })).unwrap();
      showToast('Grey menu theme updated successfully!', 'success');
    } catch (error) {
      showToast('Failed to update grey menu theme', 'error');
      setSelectedGreyColor(greyMenuColor);
    }
  };

  const handleGreyCustomColorApply = async () => {
    const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    if (!hexColorRegex.test(customGreyColor)) {
      showToast('Please enter a valid hex color code (e.g., #6b7280)', 'error');
      return;
    }
    setSelectedGreyColor('custom');
    setShowGreyCustomPicker(false);
    try {
      await dispatch(updateGreyMenuColor({ greyMenuColor: 'custom', customGreyMenuColor: customGreyColor })).unwrap();
      showToast('Custom grey menu theme applied successfully!', 'success');
    } catch (error) {
      showToast('Failed to apply custom grey menu theme', 'error');
      setSelectedGreyColor(greyMenuColor);
    }
  };

  const getCurrentGreyColorInfo = () => {
    if (selectedGreyColor === 'custom') {
      return {
        name: 'Custom Grey Color',
        value: 'custom',
        gradient: '',
        solid: customGreyColor,
        description: 'Your personalized grey theme'
      };
    }
    return colorOptions.find(option => option.value === selectedGreyColor) || colorOptions[7]; // Default to gray
  };

  const handleResetToDefaults = async () => {
    try {
      // Reset main menu to blue
      await dispatch(updateMenuColor({ menuColor: 'blue', customMenuColor: null })).unwrap();
      // Reset grey menu to table header color
      await dispatch(updateGreyMenuColor({ greyMenuColor: 'custom', customGreyMenuColor: '#f9fafb' })).unwrap();
      
      // Update local state
      setSelectedColor('blue');
      setCustomColor('#1d4ed8');
      setShowCustomPicker(false);
      setSelectedGreyColor('custom');
      setCustomGreyColor('#f9fafb');
      setShowGreyCustomPicker(false);
      
      showToast('Colors reset to defaults successfully!', 'success');
    } catch (error) {
      showToast('Failed to reset colors to defaults', 'error');
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header Section */}
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl mb-4">
          <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h10a2 2 0 012 2v12a4 4 0 01-4 4H7zM7 3H5v14a2 2 0 002 2h6a2 2 0 002-2V3H7z" />
            <circle cx="12" cy="8" r="2" />
            <circle cx="12" cy="16" r="1" />
          </svg>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Customize Your Experience</h2>
        <p className="text-gray-600 max-w-md mx-auto">
          Personalize your workspace with beautiful color themes that reflect your style and enhance your productivity.
        </p>
      </div>

      {/* Reset to Defaults Section */}
      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-200">
        <div className="flex items-center justify-between">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <div className="ml-4">
              <h4 className="text-lg font-medium text-amber-900 mb-1">Reset to Default Colors</h4>
              <p className="text-sm text-amber-800">
                Restore both main menu (Ocean Blue) and secondary menu (Table Header) to their original default colors.
              </p>
            </div>
          </div>
          <button
            onClick={handleResetToDefaults}
            disabled={loading}
            className={`ml-4 flex-shrink-0 px-4 py-2 text-white rounded-lg transition-colors text-sm font-medium ${
              loading ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-md'
            }`}
            style={{
              backgroundColor: buttonColors.primary,
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = buttonColors.primaryHover;
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = buttonColors.primary;
              }
            }}
          >
            {loading ? 'Resetting...' : 'Reset to Defaults'}
          </button>
        </div>

        {/* Default Colors Preview */}
        <div className="mt-4 flex items-center space-x-4 pl-10">
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 rounded-lg shadow-sm" style={{ backgroundColor: '#1d4ed8' }}></div>
            <span className="text-sm text-amber-800">Main Menu (Ocean Blue)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 rounded-lg shadow-sm" style={{ backgroundColor: '#f9fafb' }}></div>
            <span className="text-sm text-amber-800">Secondary Menu (Table Header)</span>
          </div>
        </div>
      </div>

      {/* Current Theme Preview */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Current Theme</h3>
          <div className="flex items-center space-x-4">
            <div 
              className="w-12 h-12 rounded-xl shadow-md"
              style={{ backgroundColor: getCurrentColorInfo().solid }}
            ></div>
            <div>
              <p className="font-medium text-gray-900">{getCurrentColorInfo().name}</p>
              <p className="text-sm text-gray-500">{getCurrentColorInfo().description}</p>
            </div>
          </div>
        </div>
        
        {/* Mini Preview */}
        <div className="p-4 bg-gray-50">
          <div className="flex items-center space-x-2">
            <div 
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: getCurrentColorInfo().solid }}
            >
              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L9 5.414V17a1 1 0 102 0V5.414l5.293 5.293a1 1 0 001.414-1.414l-7-7z" />
              </svg>
            </div>
            <span className="text-sm text-gray-600">Preview of navigation menu</span>
          </div>
        </div>
      </div>

      {/* Theme Selection */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Choose Your Theme</h3>
          <button
            onClick={() => setShowCustomPicker(!showCustomPicker)}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h10a2 2 0 012 2v12a4 4 0 01-4 4H7z" />
            </svg>
            Custom Color
          </button>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Show custom color card if one is active */}
          {selectedColor === 'custom' && (
            <div
              className="group relative overflow-hidden rounded-xl border-2 border-blue-500 shadow-lg scale-105 cursor-pointer"
            >
              {/* Custom Color Background */}
              <div 
                className="h-24 relative"
                style={{ backgroundColor: customColor }}
              >
                {/* Selection Indicator */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                </div>
              </div>
              
              {/* Theme Info */}
              <div className="p-4 bg-white">
                <h4 className="font-medium text-gray-900 mb-1">Custom Color</h4>
                <p className="text-xs text-gray-500">Your personalized theme</p>
              </div>
            </div>
          )}
          
          {colorOptions.map((option) => (
            <div
              key={option.value}
              className={`
                group relative overflow-hidden rounded-xl border-2 transition-all duration-300 cursor-pointer
                ${selectedColor === option.value 
                  ? 'border-blue-500 shadow-lg scale-105' 
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-md hover:scale-102'
                }
                ${loading ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              onClick={() => !loading && handleColorChange(option.value)}
            >
              {/* Gradient Background */}
              <div className={`h-24 bg-gradient-to-br ${option.gradient} relative`}>
                {/* Selection Indicator */}
                {selectedColor === option.value && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-lg">
                      <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                )}
                
                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 transition-all duration-200"></div>
              </div>
              
              {/* Theme Info */}
              <div className="p-4 bg-white">
                <h4 className="font-medium text-gray-900 mb-1">{option.name}</h4>
                <p className="text-xs text-gray-500">{option.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Custom Color Picker */}
        {showCustomPicker && (
          <div className="mt-6 p-6 bg-gray-50 rounded-xl border border-gray-200">
            <h4 className="font-medium text-gray-900 mb-4">Custom Color</h4>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <input
                  type="color"
                  value={customColor}
                  onChange={(e) => setCustomColor(e.target.value)}
                  className="w-12 h-12 rounded-lg border-2 border-gray-300 cursor-pointer"
                />
                <div>
                  <input
                    type="text"
                    value={customColor}
                    onChange={(e) => setCustomColor(e.target.value)}
                    placeholder="#1d4ed8"
                    className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter a hex color code</p>
                </div>
              </div>
              <button
                onClick={handleCustomColorApply}
                disabled={loading}
                className={`px-4 py-2 text-white rounded-lg transition-colors text-sm font-medium ${
                  loading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                style={{
                  backgroundColor: buttonColors.primary,
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.backgroundColor = buttonColors.primaryHover;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.target.style.backgroundColor = buttonColors.primary;
                  }
                }}
              >
                {loading ? 'Applying...' : 'Apply'}
              </button>
            </div>
            <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-green-500 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-green-800">Custom Colors Available</p>
                  <p className="text-sm text-green-700">Create your perfect theme with any color you want. Changes apply instantly to your navigation menu.</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Grey Menu Section */}
      <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Secondary Menu Theme</h3>
          <p className="text-sm text-gray-600">Customize the color of your secondary navigation menu</p>
        </div>

        {/* Current Grey Theme Preview */}
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Current Secondary Theme</h3>
          <div className="flex items-center space-x-4">
            <div 
              className="w-12 h-12 rounded-xl shadow-md"
              style={{ backgroundColor: getCurrentGreyColorInfo().solid }}
            ></div>
            <div>
              <p className="font-medium text-gray-900">{getCurrentGreyColorInfo().name}</p>
              <p className="text-sm text-gray-500">{getCurrentGreyColorInfo().description}</p>
            </div>
          </div>
        </div>
        
        {/* Grey Menu Preview */}
        <div className="p-4 bg-gray-50">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">Leads & Estimates</div>
            <div className="space-y-2">
              <div className="flex items-center space-x-3 p-2 bg-white rounded-lg border border-gray-100">
                <div 
                  className="w-5 h-5 flex items-center justify-center"
                  style={{ color: getCurrentGreyColorInfo().solid }}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                  </svg>
                </div>
                <span className="text-sm font-medium" style={{ color: getCurrentGreyColorInfo().solid }}>New Lead</span>
                <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-[3px] h-[60%] rounded-r-full"
                     style={{ backgroundColor: getCurrentGreyColorInfo().solid }}></div>
              </div>
              <div className="flex items-center space-x-3 p-2">
                <div className="w-5 h-5 text-gray-500">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-4 h-4">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
                  </svg>
                </div>
                <span className="text-sm text-gray-600">Open Leads</span>
              </div>
            </div>
          </div>
        </div>

        {/* Grey Theme Selection */}
        <div className="p-6">
          <h4 className="text-base font-medium text-gray-900 mb-4">Choose Secondary Menu Theme</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {colorOptions.map((option) => (
              <div
                key={option.value}
                onClick={() => handleGreyColorChange(option.value)}
                className={`group relative overflow-hidden rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                  selectedGreyColor === option.value
                    ? 'border-blue-500 shadow-lg scale-105'
                    : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                }`}
              >
                <div
                  className="h-20 relative"
                  style={{ backgroundColor: option.solid }}
                >
                  {selectedGreyColor === option.value && (
                    <div className="absolute inset-0 bg-black bg-opacity-10 flex items-center justify-center">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  )}
                </div>
                <div className="p-3 bg-white">
                  <h4 className="font-medium text-gray-900 text-sm mb-1">{option.name}</h4>
                  <p className="text-xs text-gray-500">{option.description}</p>
                </div>
              </div>
            ))}

            {/* Custom Grey Color Option */}
            <div
              onClick={() => setShowGreyCustomPicker(!showGreyCustomPicker)}
              className={`group relative overflow-hidden rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                showGreyCustomPicker
                  ? 'border-blue-500 shadow-lg scale-105'
                  : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
              }`}
            >
              <div className="h-20 bg-gradient-to-br from-gray-400 via-gray-500 to-gray-600 flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
                </svg>
              </div>
              <div className="p-3 bg-white">
                <h4 className="font-medium text-gray-900 text-sm mb-1">Custom</h4>
                <p className="text-xs text-gray-500">Pick any color</p>
              </div>
            </div>
          </div>
        </div>

        {/* Grey Custom Color Picker */}
        {showGreyCustomPicker && (
          <div className="mt-6 p-6 bg-gray-50 rounded-xl border border-gray-200">
            <h4 className="text-base font-medium text-gray-900 mb-4">Custom Secondary Menu Color</h4>
            <div className="flex items-center space-x-4 mb-4">
              <input
                type="color"
                value={customGreyColor}
                onChange={(e) => setCustomGreyColor(e.target.value)}
                className="w-12 h-12 rounded-lg border-2 border-gray-300 cursor-pointer"
              />
              <div className="flex-1">
                <input
                  type="text"
                  value={customGreyColor}
                  onChange={(e) => setCustomGreyColor(e.target.value)}
                  placeholder="#6b7280"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <button
              onClick={handleGreyCustomColorApply}
              disabled={loading}
              className={`px-4 py-2 text-white rounded-lg transition-colors text-sm font-medium ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              style={{
                backgroundColor: buttonColors.primary,
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = buttonColors.primaryHover;
                }
              }}
              onMouseLeave={(e) => {
                if (!loading) {
                  e.target.style.backgroundColor = buttonColors.primary;
                }
              }}
            >
              {loading ? 'Applying...' : 'Apply'}
            </button>

            <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-green-500 mt-0.5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="text-sm font-medium text-green-800">Custom Colors Available</p>
                  <p className="text-sm text-green-700">Create your perfect secondary menu theme with any color you want. Changes apply instantly to your secondary navigation menu.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Custom Grey Color Card in Grid */}
        {selectedGreyColor === 'custom' && (
          <div
            className="group relative overflow-hidden rounded-xl border-2 border-blue-500 shadow-lg scale-105 cursor-pointer"
          >
            <div
              className="h-20 relative"
              style={{ backgroundColor: customGreyColor }}
            >
              <div className="absolute inset-0 bg-black bg-opacity-10 flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div className="p-3 bg-white">
              <h4 className="font-medium text-gray-900 text-sm mb-1">Custom Grey Color</h4>
              <p className="text-xs text-gray-500">Your personalized secondary theme</p>
            </div>
          </div>
        )}
      </div>

      {/* Tips Section */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-4">
            <h4 className="text-lg font-medium text-blue-900 mb-2">Pro Tips</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• Your theme preference is saved automatically and synced across all devices</li>
              <li>• Changes take effect immediately - no need to refresh the page</li>
              <li>• Darker themes can help reduce eye strain during long work sessions</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppearanceTab;
